import { useLocation, useNavigate, useParams } from "react-router-dom";
import { HtmlGameView } from "@onmo/html-game";
import { useStyles } from "./GamePlay.style";
import {
  CasualHtmlLaunchData,
  endGamePlayGql,
  EndGamePlayOutput,
  GamePlayMode,
  GameSessionInputStatus,
  GameType,
  joinGamePlayGQL,
  JoinGamePlayOutput,
  Mutation,
  meGQL,
  achievementsGQL,
  Achievement,
} from "@onmo/graphql/graphql";
import {
  HtmlGameData,
  useInitializeGame,
  HtmlGameCallbacks,
  OnmoHtmlGame,
  ExternalGameMessage,
} from "@onmo/html-game";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useAppDispatch, useAppSelector } from "../../components/hooks";
import { GameLoading } from "../../components/common/GameLoading/GameLoading";
import { Time } from "../../models/time";
import { NotchItem } from "../../components/common/NotchItem/NotchItem";
import { NotchMenu } from "../../components/common/NotchMenu/NotchMenu";
import { PageRoutes } from "../../constants/historyGaming";
import { User } from "../../models/user";
import { RewardScreen } from "../../components/common/RewardScreen";
import { updateUser, updateAchievements } from "../../store/slices/user";
import { PRODUCT_QUERY } from "../../constants/achievement";

const GamePlay: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: gameId } = useParams<{ id: string }>();
  const { user } = useAppSelector((state) => state.user);
  const [joinGamePlayMutation] = useMutation<Mutation>(joinGamePlayGQL);
  const [endGamePlayMutation] = useMutation<Mutation>(endGamePlayGql, {});
  const { data: dataMe, refetch } = useQuery(meGQL);
  const { data: dataAchievements } = useQuery(achievementsGQL, {
    variables: {
      input: { products: PRODUCT_QUERY },
    },
  });
  const location = useLocation();
  const [gamePlayData, setGamePlayData] = useState<JoinGamePlayOutput>();
  const [gameResultData, setGameResultData] = useState<EndGamePlayOutput>();
  const [openNotchMenu, setOpenNotchMenu] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [isOpenReward, setOpenReward] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [score, setScore] = useState(0);
  const [openEndScreen, setOpenEndScreen] = useState(false);

  const htmlGameData: HtmlGameData | null = useMemo(() => {
    if (!gamePlayData || !!gameResultData) return null;
    const currentGameData = gamePlayData.gameSession.game;
    if (!currentGameData) return null;

    const launchData = gamePlayData.gameSession
      .launchData as CasualHtmlLaunchData;
    if (currentGameData.type === GameType.IframeHtml) {
      return {
        type: GameType.IframeHtml,
        url: launchData?.launchUrl,
      };
    } else {
      return {
        type: GameType.DirectHtml,
        bundleURL: launchData?.launchUrl,
        listMomentData: JSON.stringify(launchData?.gameConfig?.stages),
        isFullGameMode: true,
      };
    }
  }, [gamePlayData, gameResultData]);

  const callbacks: HtmlGameCallbacks = useMemo(() => {
    const handleEndGame = (_controller: OnmoHtmlGame, score?: number) => {
      console.log("Game ended with score:", score);
    };

    const handleScore = (_controller: OnmoHtmlGame, score?: number) => {
      setScore(score || 0);
    };

    const handleRevive = (message: ExternalGameMessage) => {
      // setEndGameMessage(message);
      // dispatch(addPopupQueue({ type: PopupType.GameLose }));
    };

    const handleHtmlGameLoaded = (_controller: OnmoHtmlGame) => {
      setLoaded(true);
    };

    return {
      onEndGame: handleEndGame,
      onHtmlGameLoaded: handleHtmlGameLoaded,
      onScore: handleScore,
      onBundleLoadingProgress: () => {},
      onClickRevive: handleRevive,
    };
  }, []);

  const gameController = useInitializeGame({
    gameData: htmlGameData || undefined,
    callbacks,
  });

  const onEndGamePlay = async () => {
    try {
      const { data } = await endGamePlayMutation({
        variables: {
          input: {
            gameSessionId: gamePlayData?.gameSession.id,
            status: GameSessionInputStatus.Completed,
            score: score,
            time: Time.getTimeDuration(startTime),
          },
        },
      });
      gameController?.unload();
      gameController?.hideCanvas();
      setOpenReward(true);
      setGameResultData(data?.endGamePlay);
    } catch (error) {
      console.error("something error when end game play", error);
    }
  };

  const onEndGame = async () => {
    setOpenNotchMenu(false);
    await onEndGamePlay();
    const dataMe = await refetch();
    const oldGzCoin = User.getGzCoin(user?.coinWallets || []);
    if (oldGzCoin !== User.getGzCoin(dataMe?.data?.me?.coinWallets)) {
      setOpenEndScreen(true);
    } else {
      navigate(PageRoutes.HomePage, { replace: true });
    }
  };

  useEffect(() => {
    const startGame = async () => {
      if (!gameId) return;
      try {
        const { data } = await joinGamePlayMutation({
          variables: {
            input: {
              gameId,
              mode: GamePlayMode.CasualHtml,
              startImmediately: true,
              modeData: {
                casualHtmlConfig: {},
              },
            },
          },
        });
        if (data?.joinGamePlay) {
          setStartTime(Date.now());
          setGamePlayData(data.joinGamePlay);
        }
      } catch (err) {
        console.error("Error starting game:", err);
        // dispatch(addPopupQueue({ type: PopupType.OutOfGems }));
      }
    };

    startGame();
  }, [gameId, joinGamePlayMutation, dispatch]);

  useEffect(() => {
    return () => {
      setGamePlayData(undefined);
      setGameResultData(undefined);
      setLoaded(false);
      setOpenReward(false);
      setScore(0);
    };
  }, [gameId]);

  useEffect(() => {
    const oldData: Record<string, number | Achievement> = {};
    if (dataMe?.me?.coinWallets) {
      dispatch(updateUser({ user: dataMe?.me }));
    }
    if (dataAchievements?.me?.achievements?.items) {
      oldData.achievements = dataAchievements?.me?.achievements?.items;
      dispatch(
        updateAchievements({
          achievements: dataAchievements?.me?.achievements?.items,
        }),
      );
    }
  }, [dataMe?.me?.coinWallets, dataAchievements?.me?.achievements?.items]);

  return (
    <div className={classes.root}>
      <div id="game-container" className={classes.gameBox}>
        {!isOpenReward && <HtmlGameView />}
      </div>
      {!isLoaded && (
        <GameLoading gameImageUrl={location.state?.gameImage || ""} />
      )}
      <NotchItem onClose={() => setOpenNotchMenu(true)} open={!openNotchMenu} />
      <NotchMenu
        onClose={() => setOpenNotchMenu(false)}
        open={openNotchMenu}
        onAction={onEndGame}
        feedId={"1"}
      />
      {openEndScreen && <RewardScreen className={classes.endScreen} />}
      {/* <OurOfGems onClose={() => navigate(PageRoutes.Home, { replace: true })} /> */}
    </div>
  );
};

export default GamePlay;
